// Passive periodic skill reducer. ADR 0014 §2.1 (機構 3) / 軸 6 §6.0。
//
// `Skill.kind === 'passive'` の発火規約:
//   - `periodTicks` 未設定 → battle init で 1 回 applyEffects (`initPeriodicSkills`)
//   - `periodTicks` 設定 → battle init で 1 回 + 周期発火 (`tickPeriodicSkills`)
//
// 状態管理: `state.periodicSkillState: Map<EntityId, Map<SkillId, Tick>>` に
// 各 entity × passive Skill の last fired tick を記録。`tickPeriodicSkills`
// は `(tick - last) >= periodTicks` を満たす Skill を再発火する。
//
// 呼び出し位置:
//   - `initPeriodicSkills`: `initBattle` の最後 (本実装は Step 9 で state.ts と
//     tick.ts に組み込む)
//   - `tickPeriodicSkills`: `tick.ts` Step 1.6 (placeable / summon tick の後)
import { evaluateActivation } from '../activation.js';
import { getSkill } from '../content/skills.js';
import { applyEffects } from '../effects.js';
/**
 * Run all passive skills on battle init.
 *
 * - 全 entity の `itemSkills` を走査
 * - `Skill.kind === 'passive'` のみ対象
 * - effects を 1 回 applyEffects (target = self)
 * - `periodTicks` 持ちは last fired tick を `state.periodicSkillState` に記録
 */
export function initPeriodicSkills(state) {
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        for (const itemSkill of entity.itemSkills) {
            const skill = getSkill(itemSkill.templateId);
            if (skill?.kind !== 'passive')
                continue;
            if (skill.periodTicks !== undefined) {
                ensureEntry(state, entity).set(skill.id, state.tick);
            }
            // ADR 0014 §6.0: passive は battle init で 1 回評価する。activation は
            // active / reactive と同じ evaluator を通し、target は self とする。
            const act = evaluateActivation(skill, entity, entity, state);
            if (!act.canFire)
                continue;
            applyEffects(skill, entity, entity, state, {
                effectAmountMod: act.effectAmountMod,
                sourceItemSkill: itemSkill,
                permanentBuffs: true,
            });
        }
    }
}
/**
 * Per-tick periodic-skill fire. Triggers each `kind: 'passive'` skill that
 * carries `periodTicks` once `(tick - last) >= periodTicks`. Updates the
 * last-fired record to `state.tick` on each fire.
 *
 * `caster.hp <= 0` のエンティティは skip (ADR 0014 §7.8 b)。
 */
export function tickPeriodicSkills(state) {
    const tick = state.tick;
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        const map = state.periodicSkillState.get(entity.id);
        if (map === undefined)
            continue;
        for (const itemSkill of entity.itemSkills) {
            const skill = getSkill(itemSkill.templateId);
            if (skill?.kind !== 'passive' || skill.periodTicks === undefined)
                continue;
            const last = map.get(skill.id) ?? 0;
            if (tick - last < skill.periodTicks)
                continue;
            const act = evaluateActivation(skill, entity, entity, state);
            if (!act.canFire)
                continue;
            applyEffects(skill, entity, entity, state, {
                effectAmountMod: act.effectAmountMod,
                sourceItemSkill: itemSkill,
                permanentBuffs: true,
            });
            map.set(skill.id, tick);
        }
    }
}
function ensureEntry(state, entity) {
    let map = state.periodicSkillState.get(entity.id);
    if (map === undefined) {
        map = new Map();
        state.periodicSkillState.set(entity.id, map);
    }
    return map;
}
//# sourceMappingURL=periodic-skills.js.map